import React, { useState } from "react";
import { formatCurrency } from "../../utils/helpers";
import SiteDrillDown from "./SiteDrillDown";

/**
 * TopSitesList component - displays top sites with drill-down functionality
 * @param {Object} props
 * @param {Array} props.topSites - Top 10 sites
 * @param {Array} props.allSites - All sites
 * @param {Array} props.snapshotData - Full snapshot data for drill-down (optional)
 */
const TopSitesList = ({ topSites = [], allSites = [], snapshotData = null }) => {
  const [showAll, setShowAll] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedCircuits, setSelectedCircuits] = useState([]);

  const displaySites = showAll ? allSites : topSites;

  const handleSiteClick = (site) => {
    if (!snapshotData) {
      // If no snapshot data, can't show drill-down
      return;
    }
    // Find all circuits for this site
    const circuits = snapshotData.filter((record) => {
      const recordSite = [
        record.std_street_address || record.street_address,
        record.std_city || record.city,
        record.std_state || record.state,
        record.std_zip || record.zip,
      ]
        .filter(Boolean)
        .join(", ");
      return recordSite === site.site;
    });
    setSelectedCircuits(circuits);
    setSelectedSite(site);
  };

  const handleCloseDrillDown = () => {
    setSelectedSite(null);
    setSelectedCircuits([]);
  };

  if (!topSites || topSites.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 tracking-tight">Top 10 Sites by Spending</h2>
              <p className="text-sm text-gray-600 mt-0.5">Sites ranked by total spending</p>
            </div>
          </div>
        </div>
        <div className="p-8 text-center">
          <p className="text-sm text-gray-500">No sites found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">
              Top {showAll ? "Sites" : "10 Sites"} by Spending
            </h2>
            {snapshotData ? (
              <p className="text-sm text-gray-600 mt-0.5">Click a site to view detailed circuit information</p>
            ) : (
              <p className="text-sm text-gray-600 mt-0.5">Sites ranked by total spending</p>
            )}
          </div>
        </div>
        {allSites.length > 10 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-900 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 transition-colors"
          >
            {showAll ? "Show top 10" : "Show all sites"}
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
          {displaySites.map((site, index) => {
            const rank = index + 1;
            const isClickable = snapshotData !== null;
            const isTopThree = rank <= 3;

            return (
              <div
                key={index}
                onClick={() => isClickable && handleSiteClick(site)}
                className={`flex items-center justify-between gap-4 p-3 rounded-xl border transition-all ${
                  isClickable
                    ? "cursor-pointer border-gray-100 hover:border-violet-200 hover:bg-violet-50/50"
                    : "border-gray-100 hover:bg-gray-50/80"
                } ${isTopThree ? "bg-gray-50/50" : ""}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span
                    className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold ${
                      rank === 1
                        ? "bg-amber-100 text-amber-700"
                        : rank === 2
                        ? "bg-slate-200 text-slate-700"
                        : rank === 3
                        ? "bg-orange-100 text-orange-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {rank}
                  </span>
                  <p className="text-sm font-medium text-gray-900 truncate flex-1">{site.site}</p>
                  {isClickable && (
                    <span className="flex-shrink-0 text-xs font-medium text-violet-600">
                      View details →
                    </span>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-sm font-semibold text-gray-900 tabular-nums">{formatCurrency(site.totalMRC)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Drill-down Modal */}
      {selectedSite && (
        <SiteDrillDown
          site={selectedSite}
          circuits={selectedCircuits}
          onClose={handleCloseDrillDown}
        />
      )}
    </div>
  );
};

export default TopSitesList;
